import { useEffect, useRef, useState } from 'react';
import { Check, Search, Users, X } from 'lucide-react';
import { User } from '../../global-components/src/types';
import { getImageUser, getImageUserNotFound } from '../../global-components/src/utils/auth';
import { useAuth } from '../../global-components/src';
import { api } from '../../services/api';
import './best-friends-picker.css';

interface BestFriendsPickerProps {
  show: boolean;
  onClose: () => void;
  onSaved: (bestFriends: User[]) => void;
}

const PAGE_SIZE = 30;

const BestFriendsPicker = ({ show, onClose, onSaved }: BestFriendsPickerProps) => {
  const { token, user } = useAuth();
  const [following, setFollowing] = useState<User[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [search, setSearch] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const requestId = useRef(0);

  const loadFollowing = async (nextPage: number, term: string, replace = false) => {
    if (!user?.id) return;

    const currentRequest = ++requestId.current;
    setLoading(true);
    try {
      const { data } = await api(token).get(`/users/following/${user.id}`, {
        params: {
          page: nextPage,
          limit: PAGE_SIZE,
          followingType: 'all',
          search: term,
        },
      });
      if (currentRequest !== requestId.current) return;

      const nextUsers: User[] = data.following ?? [];
      setFollowing(previous => {
        const all = replace ? nextUsers : [...previous, ...nextUsers];
        return Array.from(new Map(all.map(item => [item.id, item])).values());
      });
      setPage(nextPage);
      setHasMore(nextUsers.length === PAGE_SIZE);
    } catch {
      if (currentRequest === requestId.current) {
        setError('Não foi possível carregar as pessoas que você segue.');
      }
    } finally {
      if (currentRequest === requestId.current) setLoading(false);
    }
  };

  useEffect(() => {
    if (!show || !user?.id) return;

    let active = true;
    setError('');
    setSearch('');
    setSearchTerm('');
    setFollowing([]);
    setPage(1);
    setHasMore(true);

    const load = async () => {
      const activeRequest = ++requestId.current;
      setLoading(true);
      try {
        const [bestFriendsResponse, followingResponse] = await Promise.all([
          api(token).get('/users/best-friends'),
          api(token).get(`/users/following/${user.id}`, {
            params: { page: 1, limit: PAGE_SIZE, followingType: 'all', search: '' },
          }),
        ]);
        if (!active || activeRequest !== requestId.current) return;

        const bestFriends: User[] = bestFriendsResponse.data.best_friends ?? [];
        const nextFollowing: User[] = followingResponse.data.following ?? [];
        setSelectedIds(bestFriends.map(friend => friend.id));
        setFollowing(nextFollowing);
        setHasMore(nextFollowing.length === PAGE_SIZE);
      } catch {
        if (active && activeRequest === requestId.current) setError('Não foi possível carregar os melhores amigos.');
      } finally {
        if (active && activeRequest === requestId.current) setLoading(false);
      }
    };

    void load();
    return () => {
      active = false;
      requestId.current += 1;
    };
  }, [show, token, user?.id]);

  useEffect(() => {
    if (!show) return;

    const timer = window.setTimeout(() => {
      setError('');
      void loadFollowing(1, search.trim(), true);
    }, 300);

    return () => window.clearTimeout(timer);
  }, [search, show]);

  const toggleUser = (id: number) => {
    setSelectedIds(previous => (
      previous.includes(id)
        ? previous.filter(selectedId => selectedId !== id)
        : [...previous, id]
    ));
  };

  const handleSave = async () => {
    if (saving) return;

    setSaving(true);
    setError('');
    try {
      const { data } = await api(token).put('/users/best-friends', {
        user_ids: selectedIds,
      });
      onSaved(data.best_friends ?? []);
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'Não foi possível salvar os melhores amigos.');
    } finally {
      setSaving(false);
    }
  };

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    if (
      loading ||
      !hasMore ||
      target.scrollTop + target.clientHeight < target.scrollHeight - 80
    ) return;

    void loadFollowing(page + 1, searchTerm);
  };

  useEffect(() => {
    setSearchTerm(search.trim());
  }, [search]);

  if (!show) return null;

  return (
    <div className="best-friends-picker-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="best-friends-picker"
        role="dialog"
        aria-modal="true"
        aria-labelledby="best-friends-picker-title"
        onMouseDown={event => event.stopPropagation()}
      >
        <header className="best-friends-picker__header">
          <div>
            <h2 id="best-friends-picker-title">Melhores amigos</h2>
            <p>Escolha entre as pessoas que você segue.</p>
          </div>
          <button type="button" className="best-friends-picker__close" onClick={onClose} aria-label="Fechar">
            <X size={20} />
          </button>
        </header>

        <label className="best-friends-picker__search">
          <Search size={18} />
          <input
            value={search}
            onChange={event => setSearch(event.target.value)}
            placeholder="Buscar pessoas que você segue"
          />
        </label>

        {error && <p className="best-friends-picker__error">{error}</p>}

        <div className="best-friends-picker__list" onScroll={handleScroll}>
          {loading && following.length === 0 ? (
            <p className="best-friends-picker__status">Carregando…</p>
          ) : following.length === 0 ? (
            <div className="best-friends-picker__empty">
              <Users size={28} />
              <p>{search ? 'Nenhuma pessoa encontrada.' : 'Você ainda não segue ninguém.'}</p>
            </div>
          ) : (
            following.map(person => {
              const selected = selectedIds.includes(person.id);
              return (
                <button
                  type="button"
                  key={person.id}
                  className="best-friends-picker__person"
                  onClick={() => toggleUser(person.id)}
                  aria-pressed={selected}
                >
                  <img
                    src={getImageUser(person.img_account, undefined, person.name)}
                    onError={event => { event.currentTarget.src = getImageUserNotFound(person.name); }}
                    alt=""
                  />
                  <span>
                    <strong>{person.name}</strong>
                    {person.user_name && <small>@{person.user_name}</small>}
                  </span>
                  <i className={selected ? 'is-selected' : ''}>{selected && <Check size={15} />}</i>
                </button>
              );
            })
          )}
          {loading && following.length > 0 && <p className="best-friends-picker__status">Carregando…</p>}
        </div>

        <footer className="best-friends-picker__footer">
          <span>{selectedIds.length} selecionado{selectedIds.length === 1 ? '' : 's'}</span>
          <button type="button" onClick={() => void handleSave()} disabled={saving}>
            {saving ? 'Salvando…' : 'Concluir'}
          </button>
        </footer>
      </section>
    </div>
  );
};

export default BestFriendsPicker;
